import { getCartRange, isChartWithCategory } from '@app/core/services/charts/plugins/tools/chart-helper';
import { Chart, ChartType, Plugin } from 'chart.js';

export enum ZoomControlsAction {
  In = 'in',
  Out = 'out',
  Reset = 'reset',
}

interface IZoomControlsOptions {
  enabled?: boolean;
  zoomStep?: number | `${number}%`;
  onZoomControlClick?: ((action: ZoomControlsAction, wrapper: Element | null) => void) | null;
  zoomInLabel?: string;
  zoomOutLabel?: string;
  resetZoomLabel?: string;
}

interface IZoomControlsState {
  renderControls: (chart: Chart, options: Required<IZoomControlsOptions>, container: HTMLElement) => void;
  updateControls: (chart: Chart, container: HTMLElement) => void;
  lastZoomLevel: number;
  currentZoomLevel: number;
  baselineStored: boolean;
  maxLineStored: boolean;
  baseline: { min: number; max: number };
  maxLine: { min: number; max: number };
  dataSignature: string;
  controlsClickHandler?: (event: Event) => void;
}

declare module 'chart.js' {
  interface Chart {
    _zoomControlsPlugin?: IZoomControlsState;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType = ChartType> {
    zoomControls?: IZoomControlsOptions;
  }
}

const zoomControlsSelector = '.chart-zoom-controls';
const zoomHelpDialogHost = '.zoom-help-dialog-host';
const resetButtonSelector = '.reset-button';
const zoomInIconSelector = '.zoom-in-icon';
const zoomInIconFilledSelector = '.zoom-in-icon-filled';
const zoomOutIconSelector = '.zoom-out-icon';
const zoomOutIconFilledSelector = '.zoom-out-icon-filled';
const zoomInButtonSelector = `button[data-action="${ZoomControlsAction.In}"]`;
const zoomOutButtonSelector = `button[data-action="${ZoomControlsAction.Out}"]`;

const disabledButtonClasses = ['opacity-40', '!cursor-not-allowed', 'pointer-events-none'];

const defaultZoomStep = 1;

export const zoomControlsPlugin: Plugin = {
  id: 'ZoomButtons',

  afterInit(chart: Chart) {
    chart._zoomControlsPlugin = {
      renderControls: renderControls,
      updateControls: updateControls,
      lastZoomLevel: 0,
      currentZoomLevel: 1,
      baselineStored: false,
      maxLineStored: false,
      baseline: { min: 0, max: 0 },
      maxLine: { min: 0, max: 0 },
      dataSignature: '',
      controlsClickHandler: undefined,
    };
  },

  afterUpdate(chart) {
    const zoomControlsState = chart._zoomControlsPlugin;
    if (!zoomControlsState) {
      return;
    }

    if (!zoomControlsState.maxLineStored) {
      const { min, max } = getCartRange(chart);
      zoomControlsState.maxLine = { min, max };
      zoomControlsState.maxLineStored = true;
    }

    if (!zoomControlsState.baselineStored) {
      const min = chart.scales['x'].min;
      const max = chart.scales['x'].max;
      zoomControlsState.baseline = { min, max };
      zoomControlsState.baselineStored = true;
    }

    queueMicrotask(() => {
      const opts = chart.config.options?.plugins?.zoomControls;
      const canvas = chart.canvas;
      const container = canvas.parentElement;

      if (!opts?.enabled || !container || !canvas) return;

      const options: Required<IZoomControlsOptions> = {
        enabled: true,
        zoomStep: opts.zoomStep ?? defaultZoomStep,
        onZoomControlClick: opts.onZoomControlClick ?? null,
        zoomInLabel: opts.zoomInLabel ?? 'Zoom in',
        zoomOutLabel: opts.zoomOutLabel ?? 'Zoom out',
        resetZoomLabel: opts.resetZoomLabel ?? 'Reset zoom',
      };

      const min = chart.scales['x'].min;
      const max = chart.scales['x'].max;

      const baseRange = zoomControlsState.baseline.max - zoomControlsState.baseline.min;
      const currentRange = max - min;
      zoomControlsState.currentZoomLevel = baseRange ? baseRange / currentRange : 1;

      const existedControls = container.querySelector(zoomControlsSelector);
      if (existedControls) {
        zoomControlsState.updateControls(chart, container);
      } else {
        zoomControlsState.renderControls(chart, options, container);
      }

      applyDisabledStates(chart, options, container);

      zoomControlsState.lastZoomLevel = zoomControlsState.currentZoomLevel;
    });
  },

  beforeUpdate(chart) {
    const zoomControlsState = chart._zoomControlsPlugin;
    if (!zoomControlsState) {
      return;
    }

    const nextSignature = JSON.stringify(chart.data.datasets.map(ds => ds.data?.length ?? 0));

    if (nextSignature !== zoomControlsState.dataSignature) {
      zoomControlsState.dataSignature = nextSignature;
      zoomControlsState.baselineStored = false;
      zoomControlsState.maxLineStored = false;
      zoomControlsState.lastZoomLevel = 0;
    }
  },

  beforeDestroy(chart: Chart) {
    const zoomControlsState = chart._zoomControlsPlugin;
    if (!zoomControlsState) {
      return;
    }

    const canvas = chart.canvas;
    const container = canvas.parentElement;
    const controls = container?.querySelector(zoomControlsSelector);
    if (zoomControlsState.controlsClickHandler) {
      controls?.removeEventListener('click', zoomControlsState.controlsClickHandler);
    }
    controls?.remove();
  },
};

function updateControls(chart: Chart, container: HTMLElement) {
  if (!chart._zoomControlsPlugin) {
    return;
  }

  const zoomControlsState = chart._zoomControlsPlugin;

  if (zoomControlsState.currentZoomLevel === zoomControlsState.lastZoomLevel) return;
  const resetButton = container.querySelector(resetButtonSelector);
  const zoomInIcon = container.querySelector(zoomInIconSelector);
  const zoomInIconFilled = container.querySelector(zoomInIconFilledSelector);
  const zoomOutIcon = container.querySelector(zoomOutIconSelector);
  const zoomOutIconFilled = container.querySelector(zoomOutIconFilledSelector);

  let elementsToHide, elementsToShow;
  if (zoomControlsState.currentZoomLevel === 1) {
    elementsToHide = [resetButton, zoomInIconFilled, zoomOutIconFilled];
    elementsToShow = [zoomInIcon, zoomOutIcon];
  } else if (zoomControlsState.currentZoomLevel > 1) {
    elementsToHide = [zoomInIcon, zoomOutIconFilled];
    elementsToShow = [zoomOutIcon, zoomInIconFilled, resetButton];
  } else {
    elementsToHide = [zoomOutIcon, zoomInIconFilled];
    elementsToShow = [zoomInIcon, zoomOutIconFilled, resetButton];
  }

  for (const element of elementsToHide) {
    element?.classList.add('hidden');
  }
  for (const element of elementsToShow) {
    element?.classList.remove('hidden');
  }
}

function renderControls(chart: Chart, options: Required<IZoomControlsOptions>, container: HTMLElement) {
  if (!chart._zoomControlsPlugin) {
    return;
  }

  const zoomControlsState = chart._zoomControlsPlugin;
  const controls = document.createElement('div');

  const hasLegend = chart.options?.plugins?.legend?.display !== false;
  const hasTitle = chart.options?.plugins?.title?.display === true;

  const marginToTopForControlsOnSmDevices =
    hasLegend && hasTitle ? 'max-sm:top-[70px]' : hasLegend || hasTitle ? 'max-sm:top-[35px]' : 'max-sm:top-[7px]';

  controls.className = `chart-zoom-controls absolute top-0 ${marginToTopForControlsOnSmDevices} right-0 flex flex-col items-center gap-2`;

  controls.innerHTML = `
    <div class="bg-(--p-neutral-500) flex flex-col rounded-lg items-center">
      <button class="group relative w-5 h-5 cursor-pointer p-[0.625rem] !box-content" data-action="${ZoomControlsAction.In}">
        <span class="p-tooltip p-tooltip-left !block right-full top-1/2 -translate-y-1/2 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <span class="p-tooltip-text !whitespace-nowrap">${options.zoomInLabel}</span>
        </span>

        <svg width="20" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" class="zoom-in-icon ${zoomControlsState.currentZoomLevel <= 1 ? '' : 'hidden'}">
          <path d="M0.599609 16.0996L4.59961 11.5996M8.59961 3.59961V9.59961M11.5946 6.60461L5.5946 6.60461M2.59961 6.59961C2.59961 9.91332 5.2859 12.5996 8.59961 12.5996C11.9133 12.5996 14.5996 9.91332 14.5996 6.59961C14.5996 3.2859 11.9133 0.599609 8.59961 0.599609C5.2859 0.599609 2.59961 3.2859 2.59961 6.59961Z" stroke="#005581" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>

        <svg width="20" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" class="zoom-in-icon-filled ${zoomControlsState.currentZoomLevel > 1 ? '' : 'hidden'}">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M8.5997 0C12.2448 0 15.1993 2.95453 15.1993 6.59961C15.1993 10.2447 12.2448 13.1992 8.5997 13.1992C7.25221 13.1992 5.99953 12.7947 4.95517 12.1016L1.04794 16.498C0.827775 16.7456 0.448889 16.768 0.201264 16.5479C-0.0462641 16.3277 -0.068535 15.9488 0.151459 15.7012L4.01962 11.3486C2.77541 10.1484 2.00009 8.46509 2.00009 6.59961C2.00009 2.95459 4.9547 9.80432e-05 8.5997 0ZM8.5997 3C8.26847 3.00016 8.00009 3.26834 8.00009 3.59961V6.00488H5.59482C5.26357 6.00498 4.99528 6.27324 4.99521 6.60449C4.99521 6.9358 5.26353 7.20498 5.59482 7.20508H8.00009V9.59961C8.00009 9.93088 8.26847 10.1991 8.5997 10.1992C8.93102 10.1992 9.20029 9.93094 9.20029 9.59961V7.20508H11.5948C11.9261 7.20501 12.1944 6.93582 12.1944 6.60449C12.1944 6.27322 11.9261 6.00495 11.5948 6.00488H9.20029V3.59961C9.20029 3.26828 8.93102 3.00007 8.5997 3Z" fill="#005581"/>
        </svg>
      </button>

      <div class="h-[1px] bg-(--p-blue-100) w-[1.75rem]"></div>

      <button class="group relative w-5 h-5 cursor-pointer p-[0.625rem] !box-content" data-action="${ZoomControlsAction.Out}">
        <span class="p-tooltip p-tooltip-left !block right-full top-1/2 -translate-y-1/2 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <span class="p-tooltip-text !whitespace-nowrap">${options.zoomOutLabel}</span>
        </span>
        <svg width="20" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" class="zoom-out-icon ${zoomControlsState.currentZoomLevel >= 1 ? '' : 'hidden'}">
          <path d="M0.599609 16.0996L4.59961 11.5996M11.5946 6.60461L5.5946 6.60461M2.59961 6.59961C2.59961 9.91332 5.2859 12.5996 8.59961 12.5996C11.9133 12.5996 14.5996 9.91332 14.5996 6.59961C14.5996 3.2859 11.9133 0.599609 8.59961 0.599609C5.2859 0.599609 2.59961 3.2859 2.59961 6.59961Z" stroke="#005581" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>

        <svg width="20" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" class="zoom-out-icon-filled ${zoomControlsState.currentZoomLevel < 1 ? '' : 'hidden'}">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M8.5997 0C12.2448 3.2735e-05 15.1993 2.95455 15.1993 6.59961C15.1993 10.2447 12.2448 13.1992 8.5997 13.1992C7.25223 13.1992 5.99952 12.7947 4.95517 12.1016L1.04794 16.498C0.827785 16.7456 0.44889 16.7679 0.201264 16.5479C-0.0462641 16.3277 -0.068535 15.9488 0.151459 15.7012L4.01962 11.3486C2.77541 10.1484 2.00009 8.46509 2.00009 6.59961C2.00009 2.95459 4.9547 9.80432e-05 8.5997 0ZM5.59482 6.00488C5.26353 6.00493 4.9943 6.27321 4.99423 6.60449C4.99423 6.93583 5.26349 7.20503 5.59482 7.20508H11.5948C11.9261 7.20501 12.1944 6.93582 12.1944 6.60449C12.1944 6.27322 11.9261 6.00495 11.5948 6.00488H5.59482Z" fill="#005581"/>
        </svg>
      </button>
    </div>

    <div class="reset-button bg-(--p-neutral-500) rounded-lg ${zoomControlsState.currentZoomLevel === 1 ? 'hidden' : ''}">
      <button class="group relative w-5 h-5 cursor-pointer p-[0.625rem] !box-content" data-action="${ZoomControlsAction.Reset}">
        <span class="p-tooltip p-tooltip-left !block right-full top-1/2 -translate-y-1/2 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <span class="p-tooltip-text !whitespace-nowrap">${options.resetZoomLabel}</span>
        </span>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3.13187 6.74254C2.98968 7.04186 3.11706 7.39977 3.41637 7.54196C3.71569 7.68414 4.0736 7.55677 4.21578 7.25746L3.67383 7L3.13187 6.74254ZM16.8686 13.2575C17.0108 12.9581 16.8834 12.6002 16.5841 12.458C16.2848 12.3159 15.9269 12.4432 15.7847 12.7425L16.3267 13L16.8686 13.2575ZM10.0003 17V16.4V17ZM3.67383 7L4.21578 7.25746C5.24361 5.09382 7.44805 3.6 10.0002 3.6V3V2.4C6.96755 2.4 4.35097 4.17628 3.13187 6.74254L3.67383 7ZM10.0002 3V3.6C12.8985 3.6 15.3483 5.52708 16.1352 8.17115L16.7103 8L17.2854 7.82885C16.3512 4.68994 13.4439 2.4 10.0002 2.4V3ZM16.3267 13L15.7847 12.7425C14.7569 14.9062 12.5524 16.4 10.0003 16.4V17V17.6C13.0329 17.6 15.6495 15.8237 16.8686 13.2575L16.3267 13ZM10.0003 17V16.4C7.44815 16.4 5.24371 14.9062 4.21588 12.7425L3.67392 13L3.13197 13.2575C4.35107 15.8237 6.96764 17.6 10.0003 17.6V17Z" fill="#005581"/>
          <path d="M17.2333 9.85422L12.5871 6.42699C14.3652 5.65122 16.1094 4.8895 17.879 4.11727L17.2333 9.85422Z" fill="#005581"/>
          <path d="M2.75986 10.1505L7.41498 13.5656C5.63216 14.3493 3.91157 15.1054 2.12907 15.8891L2.75986 10.1505Z" fill="#005581"/>
        </svg>
      </button>
    </div>
  `;

  container.appendChild(controls);

  zoomControlsState.controlsClickHandler = e => {
    const button = (e.target as HTMLElement).closest('button');
    if (!button || button.disabled) return;
    const action = button.dataset['action'];
    const min = chart.scales['x'].min;
    const max = chart.scales['x'].max;

    const zoomStep = getZoomStep(options.zoomStep, min, max);

    if (action === ZoomControlsAction.In) {
      const nextMax = max - zoomStep;
      const nextMin = min + zoomStep;
      chart.zoomScale('x', { min: nextMin, max: nextMax }, 'zoom');
    }
    if (action === ZoomControlsAction.Out) {
      let nextMax = max + zoomStep;
      let nextMin = min - zoomStep;

      if (nextMin < zoomControlsState.maxLine.min) {
        nextMin = zoomControlsState.maxLine.min;
        nextMax += zoomStep;
      }

      if (nextMax > zoomControlsState.maxLine.max) {
        nextMax = zoomControlsState.maxLine.max;
        nextMin -= zoomStep;
      }

      if (nextMin < zoomControlsState.maxLine.min) {
        nextMin = zoomControlsState.maxLine.min;
      }

      if (nextMax > zoomControlsState.maxLine.max) {
        nextMax = zoomControlsState.maxLine.max;
      }

      chart.zoomScale('x', { min: nextMin, max: nextMax }, 'zoom');
    }
    if (action === ZoomControlsAction.Reset) {
      const baseRange = zoomControlsState.baseline.max - zoomControlsState.baseline.min;
      const currentMin = chart.scales['x'].min;
      const currentRange = chart.scales['x'].max - chart.scales['x'].min;

      const zoomOffset = (baseRange - currentRange) / 2;
      let nextMin = currentMin - zoomOffset;
      let nextMax = nextMin + baseRange;

      if (nextMin < zoomControlsState.baseline.min) {
        nextMin = zoomControlsState.baseline.min;
        nextMax = nextMin + baseRange;
      }

      if (nextMax > zoomControlsState.baseline.max) {
        nextMax = zoomControlsState.baseline.max;
        nextMin = Math.max(zoomControlsState.baseline.min, nextMax - baseRange);
      }

      chart.zoomScale('x', { min: nextMin, max: nextMax }, 'zoom');
    }

    if (options.onZoomControlClick) {
      const container = chart.canvas.parentElement;
      const existedControls = container ? container.querySelector(zoomHelpDialogHost) : null;
      options.onZoomControlClick(action as ZoomControlsAction, existedControls);
    }
  };

  controls.addEventListener('click', zoomControlsState.controlsClickHandler);
}

function applyDisabledStates(chart: Chart, options: Required<IZoomControlsOptions>, container: HTMLElement): void {
  const zoomControlsState = chart._zoomControlsPlugin;
  if (!zoomControlsState) {
    return;
  }

  const isCategoryChart = isChartWithCategory(chart);

  const min = chart.scales['x'].min;
  const max = chart.scales['x'].max + (isCategoryChart ? 1 : 0);
  const epsilon = Math.max(Math.abs(zoomControlsState.maxLine.max - zoomControlsState.maxLine.min), 1) * 1e-6;

  const zoomOutDisabled =
    min <= zoomControlsState.maxLine.min + epsilon && max >= zoomControlsState.maxLine.max - epsilon;

  const zoomStep = getZoomStep(options.zoomStep, min, max);
  const zoomInDisabled = min + zoomStep >= max - zoomStep - epsilon;

  const zoomInButton = container.querySelector<HTMLButtonElement>(zoomInButtonSelector);
  const zoomOutButton = container.querySelector<HTMLButtonElement>(zoomOutButtonSelector);

  setButtonDisabled(zoomInButton, zoomInDisabled);
  setButtonDisabled(zoomOutButton, zoomOutDisabled);
}

function setButtonDisabled(button: HTMLButtonElement | null, disabled: boolean): void {
  if (!button) {
    return;
  }

  button.disabled = disabled;
  button.classList.toggle(disabledButtonClasses[0], disabled);
  button.classList.toggle(disabledButtonClasses[1], disabled);
  button.classList.toggle(disabledButtonClasses[2], disabled);
}

function getZoomStep(zoomStep: number | `${number}%`, min: number, max: number): number {
  if (typeof zoomStep === 'string') {
    if (zoomStep.includes('%')) {
      const percentage = +zoomStep.replace('%', '').trim();

      if (!percentage) {
        return defaultZoomStep;
      }

      const value = Math.ceil((Math.abs(max - min) / 100) * percentage);

      return value > 0 ? value : 1;
    }

    return +zoomStep || defaultZoomStep;
  }

  return zoomStep;
}
